import { Skeleton } from "@/components/ui/skeleton";

/**
 * Skeleton for a template card in grid view.
 */
export function TemplateCardSkeleton() {
  return (
    <div className="animate-in fade-in border-ds-border-default bg-ds-surface-card flex flex-col overflow-hidden rounded-xl border duration-300">
      {/* Preview image skeleton */}
      <Skeleton className="h-[160px] w-full rounded-none" />

      {/* Card content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Title */}
        <Skeleton className="mb-2 h-5 w-3/4" />
        {/* Description */}
        <Skeleton className="mb-1 h-4 w-full" />
        <Skeleton className="mb-3 h-4 w-2/3" />
        {/* Tags */}
        <div className="mt-auto flex items-center gap-2">
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for templates grid.
 */
export function TemplatesGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <TemplateCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for a connector row.
 */
export function ConnectorRowSkeleton() {
  return (
    <div className="animate-in fade-in border-ds-border-default bg-ds-surface-card flex items-center gap-4 rounded-xl border p-4 duration-300">
      {/* Icon */}
      <Skeleton className="h-12 w-12 flex-shrink-0 rounded-lg" />
      {/* Content */}
      <div className="flex flex-1 flex-col gap-1">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-64" />
      </div>
      {/* Action button */}
      <Skeleton className="h-9 w-24 rounded-lg" />
    </div>
  );
}

/**
 * Skeleton for connectors list.
 */
export function ConnectorsListSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <ConnectorRowSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for an integration card.
 */
export function IntegrationCardSkeleton() {
  return (
    <div className="animate-in fade-in border-ds-border-default bg-ds-surface-card flex flex-col gap-3 rounded-xl border p-4 duration-300">
      {/* Icon + Title row */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <Skeleton className="h-5 w-32" />
      </div>
      {/* Description */}
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      {/* Tags */}
      <div className="mt-1 flex items-center gap-2">
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
    </div>
  );
}

/**
 * Skeleton for integrations grid.
 */
export function IntegrationsGridSkeleton({ count = 9 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <IntegrationCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for an app card.
 */
export function AppCardSkeleton() {
  return (
    <div className="animate-in fade-in border-ds-border-default bg-ds-surface-card flex items-center gap-4 rounded-xl border p-4 duration-300">
      {/* Logo */}
      <Skeleton className="h-14 w-14 flex-shrink-0 rounded-xl" />
      {/* Content */}
      <div className="flex flex-1 flex-col gap-1">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-48" />
        {/* Stats */}
        <div className="mt-1 flex items-center gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for apps list.
 */
export function AppsListSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <AppCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for database row.
 */
export function DatabaseRowSkeleton() {
  return (
    <div className="animate-in fade-in border-ds-border-default bg-ds-surface-card flex items-center gap-4 rounded-xl border p-4 duration-300">
      {/* Icon */}
      <Skeleton className="h-10 w-10 flex-shrink-0 rounded-lg" />
      {/* Content */}
      <div className="flex flex-1 flex-col gap-1">
        <Skeleton className="h-5 w-48" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}

/**
 * Skeleton for databases list.
 */
export function DatabasesListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <DatabaseRowSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for a webhook table row.
 */
export function WebhookTableRowSkeleton() {
  return (
    <tr className="animate-in fade-in border-ds-border-default border-b duration-300">
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-20" />
        </div>
      </td>
      <td className="px-4 py-4">
        <Skeleton className="h-6 w-16 rounded-full" />
      </td>
      <td className="px-4 py-4">
        <Skeleton className="h-4 w-24" />
      </td>
      <td className="px-4 py-4">
        <Skeleton className="h-4 w-20" />
      </td>
      <td className="px-4 py-4">
        <Skeleton className="h-6 w-6 rounded-lg" />
      </td>
    </tr>
  );
}

/**
 * Skeleton for webhooks table.
 */
export function WebhooksTableSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="border-ds-border-default overflow-hidden rounded-xl border">
      <table className="w-full">
        <thead>
          <tr className="border-ds-border-default dark:bg-ds-neutral-900/50 border-b bg-zinc-50">
            <th className="table-header px-4 py-3 text-left">Event ID</th>
            <th className="table-header px-4 py-3 text-left">Platform</th>
            <th className="table-header px-4 py-3 text-left">Status</th>
            <th className="table-header px-4 py-3 text-left">Workflow</th>
            <th className="table-header px-4 py-3 text-left">Received</th>
            <th className="w-12 px-4 py-3" />
          </tr>
        </thead>
        <tbody className="bg-ds-surface-card">
          {Array.from({ length: count }).map((_, i) => (
            <WebhookTableRowSkeleton key={i} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Skeleton for a metrics stat card.
 */
export function MetricsStatCardSkeleton() {
  return (
    <div className="animate-in fade-in border-ds-border-default bg-ds-surface-card rounded-xl border p-6 duration-300">
      <div className="flex items-center gap-4">
        <Skeleton className="h-6 w-6 flex-shrink-0 rounded" />
        <div className="min-w-0 flex-1">
          <Skeleton className="mb-2 h-3 w-20" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for metrics page content.
 */
export function MetricsPageSkeleton() {
  return (
    <div className="animate-in fade-in duration-300">
      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricsStatCardSkeleton />
        <MetricsStatCardSkeleton />
        <MetricsStatCardSkeleton />
        <MetricsStatCardSkeleton />
      </div>

      {/* Charts Row */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chart area */}
        <div className="lg:col-span-2">
          <Skeleton className="mb-6 h-4 w-32" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
        {/* Failure rate widget */}
        <div>
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>

      {/* Tabs skeleton */}
      <div className="mb-8">
        <div className="border-ds-border-default mb-6 flex gap-4 border-b">
          <Skeleton className="h-10 w-28" />
          <Skeleton className="h-10 w-28" />
        </div>
        {/* Table skeleton */}
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for billing stat card.
 */
export function BillingStatCardSkeleton() {
  return (
    <div className="animate-in fade-in border-ds-border-default bg-ds-surface-card rounded-xl border p-6 duration-300">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="flex-1">
          <Skeleton className="mb-2 h-3 w-24" />
          <Skeleton className="h-7 w-32" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for billing page content.
 */
export function BillingPageSkeleton() {
  return (
    <div className="animate-in fade-in duration-300">
      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <BillingStatCardSkeleton />
        <BillingStatCardSkeleton />
        <BillingStatCardSkeleton />
      </div>

      {/* Usage Chart */}
      <div className="border-ds-border-default bg-ds-surface-card mb-8 rounded-xl border p-6">
        <Skeleton className="mb-4 h-5 w-24" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>

      {/* Cost Table */}
      <div className="border-ds-border-default bg-ds-surface-card rounded-xl border p-6">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-5 w-28" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for a member row.
 */
export function MemberRowSkeleton() {
  return (
    <div className="animate-in fade-in flex items-center justify-between p-6 duration-300">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div>
          <Skeleton className="mb-1 h-5 w-32" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      <Skeleton className="h-8 w-20 rounded-lg" />
    </div>
  );
}

/**
 * Skeleton for members list.
 */
export function MembersListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="divide-ds-neutral-100 dark:divide-ds-neutral-800 divide-y">
      {Array.from({ length: count }).map((_, i) => (
        <MemberRowSkeleton key={i} />
      ))}
    </div>
  );
}
